/**
 * Shared frame-health plumbing for the live-video players.
 *
 * <h3>Why this is a module, not per-player state</h3>
 *
 * Both `<WhepVideoPlayer/>` and `<MseVideoPlayer/>` need to answer the same
 * question — "is this `<video>` actually advancing frames, or is it wedged on
 * a stale last-frame?" — on a periodic tick. A dense 16-up wall renders 16
 * tiles; if each tile owned its own `setInterval`, the wall would spin up 16
 * timers all firing at slightly different phases. That is exactly the kind of
 * per-tile churn memory `project_live_demo_audit_2026_07_07` flags (C4 render
 * discipline). Instead every player *subscribes* one sampler callback to a
 * single module-level scheduler: ONE `setInterval` drives all tiles, and it
 * only exists while at least one tile is mounted.
 *
 * The `FrameHealth` vocabulary and the stall threshold live here too so the
 * two players report the exact same `data-frame-health` states — the AI console
 * gates detection-overlay rendering on `frameHealth === 'flowing'`, and that
 * gate must mean the same thing regardless of which transport drew the frame.
 */

/**
 * - `waiting` — no confirmed frame yet (initial connect / buffering).
 * - `flowing` — frames are advancing; overlays may draw.
 * - `stalled` — playback was confirmed but frames stopped advancing.
 */
export type FrameHealth = 'waiting' | 'flowing' | 'stalled';

/**
 * Grace period after the last observed frame-count / currentTime advance
 * before a confirmed-playing tile is reported `stalled`. Long enough to ride
 * out a single dropped GOP on a dense wall, short enough that a genuinely
 * wedged tile stops drawing stale detection boxes within a couple of seconds.
 */
export const FRAME_STALL_AFTER_MS = 2_500;

/** Cadence of the shared frame-health sampler tick. */
export const FRAME_HEALTH_SAMPLE_INTERVAL_MS = 500;

/**
 * `<video>` narrowed to the optional `requestVideoFrameCallback` surface. Used
 * by the WHEP player to confirm first-frame paint; Chromium/WebKit expose it,
 * Firefox (older) does not — hence optional.
 */
export type VideoFrameCallbackVideoElement = HTMLVideoElement & {
  requestVideoFrameCallback?: (callback: () => void) => number;
  cancelVideoFrameCallback?: (handle: number) => void;
};

/**
 * `<video>` narrowed to the optional `getVideoPlaybackQuality` surface. The
 * `totalVideoFrames` counter is the most reliable "are frames advancing?"
 * signal; when it is unavailable we fall back to `currentTime` deltas.
 */
export type VideoPlaybackQualityVideoElement = HTMLVideoElement & {
  getVideoPlaybackQuality?: () => { totalVideoFrames?: number; droppedVideoFrames?: number };
};

const frameHealthSubscribers = new Set<() => void>();
let frameHealthScheduler: ReturnType<typeof setInterval> | null = null;

function runFrameHealthSubscribers(): void {
  // Snapshot to an array so a sampler that unsubscribes mid-tick (e.g. a tile
  // unmounting) cannot mutate the Set we are iterating.
  for (const sample of Array.from(frameHealthSubscribers)) sample();
}

/**
 * Register a frame-health sampler on the shared scheduler. Returns an
 * unsubscribe function; when the last subscriber leaves, the underlying
 * `setInterval` is torn down so no timer lingers on an empty wall.
 */
export function subscribeFrameHealth(sample: () => void): () => void {
  frameHealthSubscribers.add(sample);
  if (frameHealthScheduler === null) {
    frameHealthScheduler = setInterval(runFrameHealthSubscribers, FRAME_HEALTH_SAMPLE_INTERVAL_MS);
  }
  return (): void => {
    frameHealthSubscribers.delete(sample);
    if (frameHealthSubscribers.size === 0 && frameHealthScheduler !== null) {
      clearInterval(frameHealthScheduler);
      frameHealthScheduler = null;
    }
  };
}

/**
 * Read the decoder's cumulative decoded-frame count, or null when the browser
 * does not expose `getVideoPlaybackQuality`. Callers treat a rising value as
 * proof of frame progress.
 */
export function readTotalVideoFrames(video: HTMLVideoElement): number | null {
  const quality = (video as VideoPlaybackQualityVideoElement).getVideoPlaybackQuality?.();
  const total = quality?.totalVideoFrames;
  return typeof total === 'number' && Number.isFinite(total) ? total : null;
}
